import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { useAuthStore } from '../store/authStore'
import { useUiStore } from '../store/uiStore'
import { LANGUAGES } from '../i18n'
import { buildLangUrl } from '../lib/lang'
import { seg, pagePath } from '../lib/localizedUrl'
import { toast } from '../lib/toast'
import api from '../lib/api'
import {
  BarChart3, History, Globe, TrendingUp, Bot, Wrench,
  LogOut, Menu, X, Shield, Moon, Sun, Sparkles, BookOpen,
  Search, Radar, ChevronDown, Lock, PenTool, LineChart,
  CreditCard, User, LogIn, UserPlus, Mail, Lightbulb, Link2
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import AuthModal from './AuthModal'

// Lokální SVG vlajky (uložené v public/flags) — emoji vlajky Windows nevykresluje
// a nechceme nic stahovat z externího serveru. Mapování jazyk → soubor vlajky.
const FLAG_CC: Record<string, string> = {
  cs: 'cz', en: 'gb', de: 'de', pl: 'pl', sk: 'sk', it: 'it', hu: 'hu', uk: 'ua',
  es: 'es', fr: 'fr', nl: 'nl', ja: 'jp',
}
function Flag({ code, className }: { code: string; className?: string }) {
  const cc = FLAG_CC[code] || code
  return (
    <img
      src={`/flags/${cc}.svg`}
      alt=""
      loading="lazy"
      className={clsx('inline-block shrink-0 object-cover rounded-[3px] shadow-sm', className)}
    />
  )
}

export default function Layout() {
  const { t, i18n } = useTranslation()
  const { isAuthenticated, user, logout } = useAuthStore()
  // Administrace jen pro vyhrazený admin účet (musí mít role=admin i správný email).
  const adminEmail = (import.meta.env.VITE_ADMIN_EMAIL as string) || 'info@seo-solution.cz'
  const isAdmin = user?.role === 'admin' && user?.email?.toLowerCase() === adminEmail.toLowerCase()
  const { openAuthModal } = useUiStore()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark')
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark)
    document.body.classList.toggle('dark', dark)
    localStorage.setItem('theme', dark ? 'dark' : 'light')
  }, [dark])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Zamkni scroll pozadí, když je otevřené mobilní menu — jinak se na mobilu
  // místo menu scrolluje stránka pod ním. Panel menu má vlastní scroll (níže).
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  // Online přítomnost — ping pro admin počítadlo (každých 60 s, tiché selhání).
  useEffect(() => {
    const ping = () => { api.post('/presence').catch(() => {}) }
    ping()
    const id = window.setInterval(ping, 60000)
    return () => window.clearInterval(id)
  }, [])

  const handleLogout = () => { logout(); navigate('/'); toast.success(t('auth.signed_out', { defaultValue: 'Odhlášeno' })) }
  const resendVerify = async () => {
    try { await api.post('/auth/resend-verification'); toast.success(t('verify.resent', { defaultValue: 'Ověřovací e-mail odeslán, zkontroluj schránku.' })) }
    catch { toast.error(t('verify.resend_failed', { defaultValue: 'Odeslání selhalo' })) }
  }
  // Přepnutí jazyka = plný reload na prefixovanou URL (/en, /de…), čeština bez prefixu.
  // Reload zajistí správný Router basename; volba se uloží do localStorage.
  const switchLang = (code: string) => {
    localStorage.setItem('i18nextLng', code)
    window.location.assign(buildLangUrl(code))
  }
  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0]
  const location = useLocation()

  // „Nástroje" mega-dropdown — otevírá se najetím i klikem
  const [toolsOpen, setToolsOpen] = useState(false)
  const toolsTimer = useRef<number | undefined>(undefined)
  const openTools = () => { window.clearTimeout(toolsTimer.current); setToolsOpen(true) }
  const closeToolsSoon = () => { toolsTimer.current = window.setTimeout(() => setToolsOpen(false), 130) }

  // Po změně route přesuň focus na hlavní obsah (WCAG 2.4.3) — čtečky/klávesnice tak ví,
  // že se obsah změnil, a nemusí znovu procházet celou hlavičku. První render přeskoč.
  const mainRef = useRef<HTMLElement>(null)
  const firstRender = useRef(true)
  useEffect(() => {
    setToolsOpen(false); setMobileOpen(false)
    if (firstRender.current) { firstRender.current = false; return }
    mainRef.current?.focus()
  }, [location.pathname])

  // Položky dropdownu — Analýza + (pro přihlášené) Sledování & správa
  const menuAnalyza = [
    { to: '/', icon: Search, title: t('nav.analysis'), desc: t('navmenu.analysis_desc') },
    { to: '/tools', icon: Wrench, title: t('nav.tools'), desc: t('navmenu.tools_desc') },
    { to: pagePath('keywordIdeas', i18n.language), icon: Lightbulb, title: t('kwideas.title', { defaultValue: 'Návrh klíčových slov' }), desc: t('navmenu.kwideas_desc', { defaultValue: 'Generátor nápadů na klíčová slova zdarma' }) },
  ]
  const menuSledovani = [
    { to: '/content-optimizer', icon: PenTool, title: t('copt.title'), desc: t('navmenu.copt_desc') },
    { to: '/gsc', icon: LineChart, title: 'Search Console', desc: t('navmenu.gsc_desc') },
    { to: '/crawl', icon: Radar, title: t('nav.crawl'), desc: t('navmenu.crawl_desc') },
    { to: '/rank', icon: TrendingUp, title: t('nav.rank'), desc: t('navmenu.rank_desc') },
    { to: '/ai-viditelnost', icon: Bot, title: t('navmenu.aivis', { defaultValue: 'AI Viditelnost' }), desc: t('navmenu.aivis_desc', { defaultValue: 'Zmiňují vás ChatGPT a další AI?' }) },
    { to: '/zpetne-odkazy', icon: Link2, title: t('navmenu.backlinks', { defaultValue: 'Zpětné odkazy' }), desc: t('navmenu.backlinks_desc', { defaultValue: 'Odkazový profil domény' }) },
    { to: '/websites', icon: Globe, title: t('navmenu.websites'), desc: t('navmenu.websites_desc') },
    { to: '/history', icon: History, title: t('nav.history'), desc: t('navmenu.history_desc') },
  ]
  const toolsActive = ['/', '/tools', '/crawl', '/rank', '/websites', '/history'].includes(location.pathname)

  const NavLink = ({ to, children, icon: Icon }: { to: string; children: React.ReactNode; icon?: any }) => {
    const active = location.pathname === to
    return (
      <Link
        to={to}
        className={clsx(
          'text-sm font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors',
          active ? 'text-primary-600 bg-primary-50 dark:bg-primary-500/10 dark:text-primary-400'
            : 'text-gray-500 hover:text-primary-500 hover:bg-gray-100/60 dark:hover:bg-white/5'
        )}
      >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {children}
      </Link>
    )
  }

  const MenuItem = ({ to, icon: Icon, title, desc }: { to: string; icon: any; title: string; desc: string }) => {
    const active = location.pathname === to
    return (
      <Link
        to={to}
        onClick={() => setToolsOpen(false)}
        className={clsx(
          'group flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all',
          active ? 'bg-primary-50 dark:bg-primary-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'
        )}
      >
        <span className={clsx(
          'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all',
          active
            ? 'bg-gradient-to-br from-primary-500 to-accent-500 text-white'
            : 'bg-gradient-to-br from-primary-500/10 to-accent-500/10 text-primary-600 dark:text-primary-400 group-hover:from-primary-500 group-hover:to-accent-500 group-hover:text-white'
        )}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className={clsx('block text-sm font-semibold', active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white')}>{title}</span>
          <span className="block text-xs text-gray-400 dark:text-gray-500 leading-snug">{desc}</span>
        </span>
      </Link>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip-link (WCAG 2.4.1) — první fokusovatelný prvek, viditelný jen při tabování */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:z-[100] focus:top-3 focus:left-3 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary-600 focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
      >
        {t('a11y.skip_to_content', { defaultValue: 'Přeskočit na obsah' })}
      </a>
      {/* Header — glassmorphism */}
      <header
        className={clsx(
          'sticky top-0 z-50 transition-all duration-300',
          scrolled
            ? 'glass border-b border-gray-200/50 shadow-sm dark:glass-dark dark:border-white/[0.06]'
            : 'bg-transparent'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center shadow-lg shadow-primary-500/25 group-hover:shadow-primary-500/40 transition-shadow">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                SEO Tools
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1" aria-label={t('a11y.main_nav', { defaultValue: 'Hlavní navigace' })}>
              {isAuthenticated && <NavLink to="/dashboard" icon={BarChart3}>{t('nav.dashboard')}</NavLink>}

              {/* Nástroje — mega dropdown */}
              <div className="relative" onMouseEnter={openTools} onMouseLeave={closeToolsSoon}>
                <button
                  onClick={() => setToolsOpen((v) => !v)}
                  aria-expanded={toolsOpen}
                  aria-haspopup="true"
                  className={clsx(
                    'text-sm font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors',
                    toolsActive || toolsOpen
                      ? 'text-primary-600 bg-primary-50 dark:bg-primary-500/10 dark:text-primary-400'
                      : 'text-gray-500 hover:text-primary-500 hover:bg-gray-100/60 dark:hover:bg-white/5'
                  )}
                >
                  <Wrench className="w-3.5 h-3.5" /> {t('navmenu.label')}
                  <ChevronDown className={clsx('w-3.5 h-3.5 transition-transform duration-200', toolsOpen && 'rotate-180')} />
                </button>
                <AnimatePresence>
                  {toolsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.16, ease: 'easeOut' }}
                      className="absolute left-0 top-full pt-2 w-[330px] z-50"
                    >
                      <div className="rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200/70 dark:border-white/10 shadow-xl shadow-primary-900/5 p-2">
                        <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('navmenu.group_analyza')}</p>
                        {menuAnalyza.map((m) => <MenuItem key={m.to} {...m} />)}

                        {isAuthenticated ? (
                          <>
                            <p className="px-3 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('navmenu.group_sledovani')}</p>
                            {menuSledovani.map((m) => <MenuItem key={m.to} {...m} />)}
                          </>
                        ) : (
                          <Link
                            to="/odemknout"
                            onClick={() => setToolsOpen(false)}
                            className="mt-1.5 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-primary-50 to-accent-500/10 dark:from-primary-500/10 dark:to-accent-500/10 text-left"
                          >
                            <span className="w-9 h-9 rounded-lg bg-white/80 dark:bg-white/10 flex items-center justify-center shrink-0">
                              <Lock className="w-4 h-4 text-primary-500" />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-gray-900 dark:text-white">{t('navmenu.guest_cta_title')}</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400 leading-snug">{t('navmenu.guest_cta_desc')}</span>
                            </span>
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavLink to={`/${seg('help', i18n.language)}`} icon={BookOpen}>{t('nav.help')}</NavLink>
              <NavLink to="/pricing" icon={CreditCard}>{t('nav.pricing')}</NavLink>

              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <NavLink to="/ai-settings" icon={Bot}>{t('nav.ai')}</NavLink>
                  )}
                  {isAdmin && (
                    <Link to="/admin" className="text-amber-500 hover:text-amber-400 transition text-sm font-medium flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5" />
                      Admin
                    </Link>
                  )}

                  <div className="w-px h-5 bg-gray-200 dark:bg-white/10 mx-1.5" />

                  <Link to="/profile" className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-500 transition">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {(user?.name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                    </div>
                    <span className="hidden lg:flex flex-col leading-tight">
                      <span className="font-medium text-gray-700 dark:text-gray-200">{user?.name || user?.email?.split('@')[0]}</span>
                      <span className="text-[11px] text-primary-500 font-semibold uppercase tracking-wider">{user?.plan}</span>
                    </span>
                  </Link>
                  <button onClick={handleLogout} className="text-gray-400 hover:text-red-400 transition p-1" title={t('nav.logout')}>
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <div className="w-px h-5 bg-gray-200 dark:bg-white/10 mx-1.5" />
                  <button onClick={() => openAuthModal('login')}
                    className="inline-flex items-center gap-1.5 text-gray-500 hover:text-primary-500 hover:bg-gray-100/60 dark:hover:bg-white/5 transition text-sm font-medium px-3 py-1.5 rounded-lg">
                    <LogIn className="w-3.5 h-3.5" /> {t('nav.login')}
                  </button>
                  <button
                    onClick={() => openAuthModal('register')}
                    className="inline-flex items-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> {t('nav.register')}
                  </button>
                </>
              )}

              {/* Dark mode */}
              <button
                onClick={() => setDark(!dark)}
                aria-label={t('a11y.toggle_theme', { defaultValue: 'Přepnout světlý/tmavý režim' })}
                className="text-gray-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-gray-100/50 transition"
              >
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Language */}
              <div className="relative">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1 text-base leading-none px-2 py-1.5 rounded-lg hover:bg-gray-100/50 dark:hover:bg-white/5 transition"
                  title={currentLang.name}
                  aria-label={t('a11y.change_language', { defaultValue: 'Změnit jazyk' }) + ': ' + currentLang.name}
                  aria-expanded={langOpen}
                  aria-haspopup="true"
                >
                  <Flag code={currentLang.code} className="w-5 h-[15px]" />
                  <ChevronDown className={clsx('w-3 h-3 text-gray-400 transition-transform duration-200', langOpen && 'rotate-180')} />
                </button>
                {langOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-zinc-900 rounded-xl shadow-xl border border-gray-200/50 dark:border-white/10 py-1 z-50 min-w-[160px] animate-fade-in">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => { switchLang(lang.code); setLangOpen(false) }}
                          className={clsx(
                            'w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition',
                            i18n.language === lang.code
                              ? 'text-primary-500 font-medium bg-primary-50 dark:bg-primary-500/10'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
                          )}
                        >
                          <Flag code={lang.code} className="w-5 h-[15px]" />
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t('a11y.menu', { defaultValue: 'Menu' })}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              className="md:hidden text-gray-500 p-2"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <nav id="mobile-menu" aria-label={t('a11y.mobile_nav', { defaultValue: 'Mobilní navigace' })} className="md:hidden border-t border-gray-200/40 dark:border-white/5 glass dark:glass-dark px-4 pt-3 pb-10 animate-fade-in max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
            <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('navmenu.group_analyza')}</p>
            <div className="space-y-0.5">
              {menuAnalyza.map(({ to, icon: Icon, title, desc }) => (
                <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-gray-100/60 dark:hover:bg-white/5 transition-colors">
                  <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/10 to-accent-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
                  <span className="min-w-0"><span className="block text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">{title}</span><span className="block text-xs text-gray-400 truncate leading-tight mt-0.5">{desc}</span></span>
                </Link>
              ))}
            </div>

            {isAuthenticated && (
              <>
                <p className="px-1 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('navmenu.group_sledovani')}</p>
                <div className="space-y-0.5">
                  {menuSledovani.map(({ to, icon: Icon, title, desc }) => (
                    <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-gray-100/60 dark:hover:bg-white/5 transition-colors">
                      <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/10 to-accent-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
                      <span className="min-w-0"><span className="block text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">{title}</span><span className="block text-xs text-gray-400 truncate leading-tight mt-0.5">{desc}</span></span>
                    </Link>
                  ))}
                </div>
              </>
            )}

            <div className="mt-3 pt-3 border-t border-gray-200/40 dark:border-white/5 space-y-0.5">
              {isAuthenticated && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2 px-1 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100/60 dark:hover:bg-white/5 hover:text-primary-500 transition-colors"><span className="w-8 flex justify-center shrink-0"><BarChart3 className="w-[18px] h-[18px]" /></span>{t('nav.dashboard')}</Link>}
              <Link to={`/${seg('help', i18n.language)}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2 px-1 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100/60 dark:hover:bg-white/5 hover:text-primary-500 transition-colors"><span className="w-8 flex justify-center shrink-0"><BookOpen className="w-[18px] h-[18px]" /></span>{t('nav.help')}</Link>
              <Link to="/pricing" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2 px-1 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100/60 dark:hover:bg-white/5 hover:text-primary-500 transition-colors"><span className="w-8 flex justify-center shrink-0"><CreditCard className="w-[18px] h-[18px]" /></span>{t('nav.pricing')}</Link>
              {isAuthenticated && <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2 px-1 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100/60 dark:hover:bg-white/5 hover:text-primary-500 transition-colors"><span className="w-8 flex justify-center shrink-0"><User className="w-[18px] h-[18px]" /></span>{t('nav.profile')}</Link>}
              {isAuthenticated && (
                <button onClick={handleLogout} className="flex items-center gap-3 w-full py-2 px-1 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"><span className="w-8 flex justify-center shrink-0"><LogOut className="w-[18px] h-[18px]" /></span>{t('nav.logout')}</button>
              )}
            </div>

            {!isAuthenticated && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => { openAuthModal('login'); setMobileOpen(false) }}
                  className="flex-1 text-center text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/10 rounded-lg py-2.5 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                  {t('nav.login')}
                </button>
                <button onClick={() => { openAuthModal('register'); setMobileOpen(false) }}
                  className="flex-1 text-center text-sm font-semibold bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg py-2.5 hover:shadow-lg hover:shadow-primary-500/25 transition-all">
                  {t('nav.register')}
                </button>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-gray-200/40 dark:border-white/5">
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setDark(!dark)} aria-label={t('a11y.toggle_theme', { defaultValue: 'Přepnout světlý/tmavý režim' })} className="text-gray-500 dark:text-gray-400 p-2 rounded-lg bg-gray-100/60 dark:bg-white/5 hover:bg-gray-200/60 dark:hover:bg-white/10 transition-colors">
                  {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{t('a11y.change_language', { defaultValue: 'Změnit jazyk' })}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { switchLang(lang.code); setMobileOpen(false) }}
                    title={lang.name}
                    aria-label={lang.name}
                    aria-pressed={i18n.language === lang.code}
                    className={clsx('flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition',
                      i18n.language === lang.code
                        ? 'border-primary-400 ring-1 ring-primary-400 bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200/60 dark:border-white/10 text-gray-600 dark:text-gray-300 opacity-80 hover:opacity-100 hover:border-primary-200'
                    )}
                  >
                    <Flag code={lang.code} className="w-5 h-[15px]" />
                    <span className="text-xs font-medium uppercase">{lang.code}</span>
                  </button>
                ))}
              </div>
            </div>
          </nav>
        )}
      </header>

      <AuthModal />

      {/* Banner: neověřený e-mail — účet je omezený, dokud uživatel neověří */}
      {isAuthenticated && user && user.email_verified === 0 && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3 flex-wrap text-sm">
            <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-amber-800 dark:text-amber-300">{t('verify.banner', { defaultValue: 'Ověřte prosím svůj e-mail, dokud to neuděláte, je účet omezený a nelze provádět akce.' })}</span>
            <button onClick={resendVerify} className="ml-auto text-amber-800 dark:text-amber-300 font-semibold underline hover:no-underline">
              {t('verify.resend', { defaultValue: 'Poslat odkaz znovu' })}
            </button>
          </div>
        </div>
      )}

      <main id="main-content" ref={mainRef} tabIndex={-1} className="flex-1 outline-none">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="cv-auto bg-gray-950 text-gray-500 py-12 mt-auto border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-accent-500 rounded-md flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-400">SEO Tools</span>
            </div>
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} SEO Tools. {t('nav.tagline')}.
            </p>
            <div className="flex flex-wrap gap-4 text-xs justify-center">
              <Link to="/tools" className="hover:text-primary-400 transition">{t('nav.tools')}</Link>
              <Link to="/blog" className="hover:text-primary-400 transition">{t('nav.blog', { defaultValue: 'Blog' })}</Link>
              <Link to="/pricing" className="hover:text-primary-400 transition">{t('nav.pricing')}</Link>
              <Link to={pagePath('comparison', i18n.language)} className="hover:text-primary-400 transition">{t('nav.comparison', { defaultValue: 'Srovnání' })}</Link>
              <Link to="/api-docs" className="hover:text-primary-400 transition">API</Link>
              <Link to={pagePath('contact', i18n.language)} className="hover:text-primary-400 transition">{t('nav.contact', { defaultValue: 'Kontakt' })}</Link>
              <Link to={pagePath('privacy', i18n.language)} className="hover:text-primary-400 transition">{t('footer.privacy')}</Link>
              <Link to={pagePath('terms', i18n.language)} className="hover:text-primary-400 transition">{t('footer.terms')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
